import { useState, type FormEvent } from 'react'
import { Table, useApiQuery, useToast, type Column } from 'grabbitbot-legacy-ui'
import { screenerClient } from '../../api'
import type { ServiceAccount } from '../../api/types'

/** Equivalent of legacy `ServiceAccountListComponent` — manage API service
 * accounts: create, reveal/rotate password, revoke (audit §3/§4). */
export function ServiceAccountsPage() {
  const { data, loading, error, refetch } = useApiQuery(() => screenerClient.searchServiceAccounts(), [])
  const roles = useApiQuery(() => screenerClient.searchServiceAccountRoles(), [])
  const { showToast } = useToast()
  const [login, setLogin] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [revealed, setRevealed] = useState<Record<string, string>>({})
  const [creating, setCreating] = useState(false)

  async function handleCreate(event: FormEvent) {
    event.preventDefault()
    setCreating(true)
    try {
      const created = await screenerClient.addServiceAccount({ login, roles: selectedRoles })
      if (created.password) setRevealed((prev) => ({ ...prev, [created.id ?? login]: created.password! }))
      setLogin('')
      setSelectedRoles([])
      refetch()
      showToast('Service account created', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not create service account', 'error')
    } finally {
      setCreating(false)
    }
  }

  // `GET /serviceAccounts/{id}/pass` is a deliberate, permanent 410 — the
  // backend never stores a recoverable secret (`app/routers/
  // service_accounts.py`, a documented security fix over the legacy
  // behaviour, which *could* derive it). A round-1 guess assumed a
  // `{ password }` 200 response here, which — since `ApiClient.get()` throws
  // on non-2xx — meant clicking "Reveal" on any existing account threw an
  // unhandled promise rejection with no user-visible feedback at all.
  async function reveal(id: string) {
    try {
      const { password } = await screenerClient.viewServiceAccountPassword(id)
      setRevealed((prev) => ({ ...prev, [id]: password }))
    } catch {
      showToast(
        'Service-account secrets are shown once at creation time and can never be retrieved again — revoke and recreate this account if the secret is lost.',
        'error',
      )
    }
  }

  async function revoke(id: string) {
    await screenerClient.revokeServiceAccount(id)
    refetch()
  }

  function toggleRole(role: string) {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]))
  }

  const columns: Column<ServiceAccount>[] = [
    { key: 'login', label: 'Login', align: 'left' },
    { key: 'roles', label: 'Roles', align: 'left', format: (v) => (Array.isArray(v) ? v.join(', ') : '') },
    { key: 'status', label: 'Status', format: (v) => (v === 1 ? 'Active' : 'Revoked') },
    {
      key: 'id',
      label: 'Password',
      sortable: false,
      align: 'left',
      format: (_v, row) =>
        row.id ? (
          revealed[row.id] ? (
            <code>{revealed[row.id]}</code>
          ) : (
            <button type="button" onClick={() => reveal(row.id!)}>
              Reveal
            </button>
          )
        ) : null,
    },
    {
      key: 'revokedAt',
      label: '',
      sortable: false,
      format: (_v, row) =>
        row.id &&
        !row.revokedAt && (
          <button type="button" onClick={() => revoke(row.id!)}>
            Revoke
          </button>
        ),
    },
  ]

  return (
    <div>
      <h1>Service accounts</h1>

      <form className="screener-form screener-card" onSubmit={handleCreate}>
        <h2>New service account</h2>
        <label>
          Login
          <input value={login} onChange={(e) => setLogin(e.target.value)} required />
        </label>
        <div className="screener-actions">
          {(roles.data ?? []).map((role) => (
            <label key={role}>
              <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => toggleRole(role)} />
              {role}
            </label>
          ))}
        </div>
        <button type="submit" disabled={creating}>
          {creating ? 'Creating…' : 'Create'}
        </button>
      </form>

      {loading && <p>Loading…</p>}
      {error && <p role="alert">Could not load service accounts.</p>}
      {data && <Table columns={columns} rows={data} rowKey={(row, i) => row.id ?? i} initialSort={{ key: 'login', dir: 'asc' }} />}
    </div>
  )
}
